import React, { Fragment, useMemo, useRef, Component } from 'react';
import { pickBy, isEqual, difference, uniq, round, get, invert, mapValues, orderBy, pick, isEmpty, last, sumBy, groupBy, keyBy, sortBy, omit, takeWhile, } from 'lodash';
import { useToggle, useAsync, useList, } from 'react-use';
import { Input, Button } from 'reactstrap';
import Select from 'react-select';
import qs from 'qs';
import numeral from 'numeral';
import { Container, Draggable } from 'react-smooth-dnd';
import { arrayMoveImmutable } from 'array-move';
import { useHistory, useLocation, } from 'react-router';
import { toast } from 'react-toastify';
import { format as formatDate, addSeconds, } from 'date-fns';
import { Tooltip } from 'react-tooltip'
import copy from 'copy-to-clipboard';

import firebase, { functions } from '../../firebase';
import { numberFormat } from '../../util';
import texts from '../../shared/texts';
import { fiscalYearOfPeriod, createOperationLog, createOperationLogs, } from '../../utils';
import { batch, getCollectionData, } from '../../shared/firebase';
import { buildChangeTypes } from '../../shared/changeTypes';
import { accountItemCategoryNames } from '../../shared/config';
import { parseSections, } from '../../shared/models/section';
import { fields, itemFields, inheritTypes, batchCloneFields, } from '../../shared/models/individualAdjustmentJournal';
import { getCategory, } from '../../shared/models/accountItem';
import { generateRows, rowsForExport, computeAlerts, } from '../../shared/lib/individualAdjustments';
import useScrollFocusByQuery from '../hooks/useScrollFocusByQuery';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import { useChangeFieldOrders } from '../hooks/useChangeFields';
import useCompanySelector from '../hooks/useCompanySelector';
import useQueryParams from '../hooks/useQueryParams';
import RelatedCompanyPage from '../hocs/RelatedCompanyPage';
import useChangeFields from '../hooks/useChangeFields';
import IndividualAdjustmentJournalFormModal from '../modals/IndividualAdjustmentJournalFormModal';
import IndividualAdjustmentJournalTemplates from '../IndividualAdjustmentJournalTemplates';
import ModelFormModal from '../modals/ModelFormModal';
import CompanySyncButton from '../CompanySyncButton';
import TrialsSyncButton from '../TrialsSyncButton';
import AddButton from '../AddButton';
import AutoLinkText from '../AutoLinkText';
import EditButton from '../EditButton';
import DeleteButton from '../DeleteButton';
import ExportButton from '../ExportButton';
import ImportButton from '../ImportButton';
import ModalButton from '../ModalButton';
import ProgressButton from '../ProgressButton';
import QuerySelector from '../QuerySelector';
import Alerts from '../Alerts';
import OperationHistoryButton from '../OperationHistoryButton';

const { entries } = Object;
const db = firebase.firestore();
const inheritTypesByLabel = invert(mapValues(inheritTypes, _ => _.label));
const approvements = {
  isApproved: { label: '承認済み', value: true, },
  isNotApproved: { label: '未承認', value: false, },
};
const approvementOptions = entries(approvements).map(([k, v]) => ({ label: v.label, value: k, }));

export default RelatedCompanyPage(function CompanyIndividualAdjustments (props) {
  const { user, company, relatedCompany, subsidiaryId, period, yearMonth, periodOptions, prevEndYearMonth, isLockedMonth, } = props;
  const { role, } = company?.users?.[user.id] || {};
  const canApprove = user?.admin || ['owner', 'admin'].includes(role) || company?.users?.[user.id]?.approvable;
  const queryParams = useQueryParams();
  const history = useHistory();
  const location = useLocation();
  const [isDragging, toggleDragging] = useToggle(false);
  const [selectedIds, { push: selectId, removeAt: removeSelectedIdAt, set: setSelectedIds, }] = useList([]);
  const unselectId = (_) => removeSelectedIdAt(selectedIds.indexOf(_));
  const isSelecting = selectedIds.length > 0;
  const individualAdjustmentJournalsRef = company.ref.collection('individualAdjustmentJournals');
  const individualAdjustmentJournals = useCollectionSubscription(individualAdjustmentJournalsRef.where('subsidiaryId', '==', subsidiaryId).where('yearMonth', '==', yearMonth), [company, subsidiaryId, yearMonth]);
  const individualAdjustmentJournalsById = keyBy(individualAdjustmentJournals, 'id');
  const individualAdjustmentJournalLogsRef = useMemo(_ => company?.ref.collection('logs').where('operationType', 'in', ['createIndividualAdjustmentJournal', 'updateIndividualAdjustmentJournal']).where('payload.yearMonth', '==', yearMonth), [company != null, yearMonth]);
  const individualAdjustmentJournalLogs = useCollectionSubscription(company?.prohibitsSelfApprovement && individualAdjustmentJournalLogsRef, [company?.prohibitsSelfApprovement, individualAdjustmentJournalLogsRef]);
  const lastOperatorsById = useMemo(_ => {
    const grouped = groupBy(individualAdjustmentJournalLogs, _ => _.payload?.individualAdjustmentJournalId);
    return mapValues(grouped, _ => last(orderBy(_, _ => _.createdAt?.toDate()))?.createdBy);
  }, [individualAdjustmentJournalLogs]);
  const { customChangeFields: allCustomChangeFields } = useChangeFields(company);
  const ordersByModelType = useChangeFieldOrders(company);
  const changeTypes = buildChangeTypes(allCustomChangeFields);
  const changeTypesByLabel = invert(mapValues(changeTypes, _ => _.label));
  const sections = useCollectionSubscription(company.ref.collection('sections').where('subsidiaryId', '==', subsidiaryId), [relatedCompany.id]);
  const { tipSections } = parseSections(sections);
  const sectionsById = keyBy(tipSections, 'id');
  const sectionsByName = keyBy(tipSections, 'name');
  const accountItems = useCollectionSubscription(company.ref.collection('accountItems'));
  const sortedAccountItems = sortBy(accountItems, _ => accountItemCategoryNames.indexOf(_.account_category), 'index');
  const accountItemsGroupedByConsolidationAccountItemId = groupBy(accountItems, 'consolidationAccountItemId');
  const consolidationAccountItems = sortedAccountItems.filter(_ => _.subsidiaryId === null);
  const mappedConsolidationAccountItems = consolidationAccountItems.filter(_ => accountItemsGroupedByConsolidationAccountItemId[_.id]);
  const consolidationAccountItemsById = keyBy(consolidationAccountItems, 'id');
  const consolidationAccountItemsByCode = keyBy(consolidationAccountItems, 'shortcut_num');
  const consolidationAccountItemsByName = keyBy(consolidationAccountItems, 'name');
  const rows = generateRows(individualAdjustmentJournals, sections, accountItems, consolidationAccountItems);
  const { elm, focusColor, } = useScrollFocusByQuery(_ => rows.map(_ => _.id).includes(queryParams.individualAdjustmentJournalId));

  let filteredRows = rows;
  let filtersContent = null;
  if(!isEmpty(queryParams.approvements)) {
    filteredRows = filteredRows.filter(r => queryParams.approvements.map(_ => approvements[_].value).includes(r.approvedAt != null));
  }
  [['debit', 'credit', '借方'], ['credit', 'debit', '貸方']].map(([direction, opponent, directionJa]) => {
    const itemIdName = direction + 'ItemId';
    if(queryParams[itemIdName]) {
      const consolidationAccountItem = consolidationAccountItemsById[queryParams[itemIdName]];
      filteredRows = filteredRows
        .map(_ => ({ ..._, items: _.items.filter(_ => _[itemIdName] === queryParams[itemIdName]).map(_ => omit(_, [opponent + 'ItemId', opponent + 'Amount', opponent + 'SectionId'])), }))
        .filter(_ => _.items.length > 0);
      const onClickClearFilters = _ => history.replace(location.pathname + '?' + qs.stringify(omit(queryParams, itemIdName)));
      filtersContent = (
        <div className="alert alert-info d-flex align-items-center gap-2">
          {directionJa}科目「{consolidationAccountItem?.name}」で絞り込み中
          <Button outline color="link" className="text-secondary" onClick={onClickClearFilters}>
            <span className="fas fa-times mr-1" />
            クリア
          </Button>
        </div>
      );
    }
  });

  const isBugyo = relatedCompany.externalType === 'bugyo';
  const validateRow = (row, _, allRows) => {
    const errors = [
      ...['debit', 'credit'].flatMap((direction) => {
        const amount = Math.abs(numeral(row[`${direction}Amount`]).value());
        const accountField = isBugyo ? `${direction}AccountItemCode` : `${direction}AccountItemName`;
        return amount > 0 && isEmpty(row[accountField]) ? [`[${accountField}] 入力してください`] :
          !isEmpty(row[accountField]) && !(isBugyo ? consolidationAccountItemsByCode[row[accountField]] : consolidationAccountItemsByName[row[accountField]]) ? [`[${accountField}] 該当の連結科目が見つかりません`] : [];
      }),
      ...(!isEmpty(row.delimiter) ? (() => {
        const groupRows = [row, ...takeWhile(allRows.slice(allRows.indexOf(row) + 1), _ => isEmpty(_.delimiter))];
        const totalDebit = round(sumBy(groupRows, _ => numeral(_.debitAmount).value()), 6);
        const totalCredit = round(sumBy(groupRows, _ => numeral(_.creditAmount).value()), 6);
        return totalDebit !== totalCredit ? ['貸借一致していません'] : [];
      })() : []),
    ];
    return { ...row, errors };
  };
  const processValidatedRows = (rows) => {
    return rows.reduce((x, y) => {
      return isEmpty(y.delimiter) ? [...x.slice(0, x.length - 1), [...last(x), y]] : [...x, [y]];
    }, [])
      .map((group, i) => {
        const [{ delimiter, inheritType = 'なし', description, }] = group;
        return {
          inheritType: inheritTypesByLabel[inheritType],
          items: group.map(({ debitAccountItemCode, debitAccountItemName, debitSectionName, debitChangeType, debitAmount, creditAccountItemCode, creditAccountItemName, creditSectionName, creditChangeType, creditAmount }) => {
            return {
              debitItemId: (isBugyo ? consolidationAccountItemsByCode[debitAccountItemCode] : consolidationAccountItemsByName[debitAccountItemName])?.id || null,
              debitSectionId: sectionsByName[debitSectionName]?.id || null,
              debitChangeType: changeTypesByLabel[debitChangeType] || null,
              debitAmount: numeral(debitAmount).value(),
              creditItemId: (isBugyo ? consolidationAccountItemsByCode[creditAccountItemCode] : consolidationAccountItemsByName[creditAccountItemName])?.id || null,
              creditSectionId: sectionsByName[creditSectionName]?.id || null,
              creditChangeType: changeTypesByLabel[creditChangeType] || null,
              creditAmount: numeral(creditAmount).value(),
            };
          }),
          description,
          index: i,
        };
      });
  };
  const importedRefsRef = useRef([]);
  const processRow = (batch, row, i, importRef) => {
    const ref = individualAdjustmentJournalsRef.doc();
    importedRefsRef.current.push(ref);
    batch.set(ref, {
      ...row,
      createdAt: new Date(),
      subsidiaryId,
      period,
      yearMonth,
      importId: importRef.id,
    }, { merge: true });
  };
  const onSubmitBatchClone = async (values, { onClickClose }) => {
    if(!window.confirm('本当にコピーしますか？')) return;

    const { yearMonth: sourceYearMonth, } = values;
    const sources = await getCollectionData(individualAdjustmentJournalsRef.where('subsidiaryId', '==', subsidiaryId).where('yearMonth', '==', sourceYearMonth));
    const createdRefs = [];
    await batch(db, sortBy(sources, 'index'), (batch, source, i) => {
      const ref = individualAdjustmentJournalsRef.doc();
      createdRefs.push(ref);
      batch.set(ref, {
        ...omit(source, ['id', 'ref', 'approvedAt', 'approvedBy', 'isUpdatedAfterCopy']),
        period,
        yearMonth,
        copiedAt: new Date(),
        sourceYearMonth,
        sourceId: source.id,
        createdAt: addSeconds(new Date(), i),
        description: (source.description || ''),
      })
    });
    await createOperationLogs(company, 'individualAdjustmentJournal', 'createIndividualAdjustmentJournal', createdRefs.map(_ => ({ yearMonth, individualAdjustmentJournalId: _.id })));
    toast.success('コピーしました');
    onClickClose();
  };
  const onSubmitCloneToOther = async (values, { onClickClose }) => {
    if(!window.confirm(`${selectedIds.length}件を他の年月にコピーします。よろしいですか？`)) return;

    const { period: toPeriod, yearMonth: toYearMonth, } = values;
    const createdRefs = [];
    await batch(db, selectedIds, (batch, sourceId, i) => {
      const source = individualAdjustmentJournalsById[sourceId];
      const ref = individualAdjustmentJournalsRef.doc();
      createdRefs.push(ref);
      batch.set(ref, {
        ...omit(source, ['id', 'ref', 'approvedAt', 'approvedBy', 'isUpdatedAfterCopy']),
        index: 1000 + i,
        period: toPeriod,
        copiedAt: new Date(),
        sourceYearMonth: yearMonth,
        sourceId,
        yearMonth: toYearMonth,
        createdAt: addSeconds(new Date(), i),
        description: (source.description || ''),
      })
    });
    await createOperationLogs(company, 'individualAdjustmentJournal', 'createIndividualAdjustmentJournal', createdRefs.map(_ => ({ yearMonth: toYearMonth, individualAdjustmentJournalId: _.id })));
    toast.success('コピーしました');
    onClickClose();
    setSelectedIds([]);
  };
  const onClickInheritFromPrevEnd = async () => {
    if(!window.confirm('前期末から開始仕訳を引き継ぎます。よろしいですか？')) return;

    const prevEndYearMonthIndividualAdjustmentJournals = await getCollectionData(individualAdjustmentJournalsRef.where('subsidiaryId', '==', subsidiaryId).where('yearMonth', '==', prevEndYearMonth));
    const items = sortBy(prevEndYearMonthIndividualAdjustmentJournals, 'index').flatMap((individualAdjustmentJournal) => {
      const { inheritType, items, } = individualAdjustmentJournal;
      const source = omit(individualAdjustmentJournal, ['id', 'ref', 'approvedAt', 'approvedBy', 'isUpdatedAfterCopy']);
      return ({
        none: [],
        carry: [
          {
            ...source,
            items: items.map((item) => {
              return {
                ...item,
                debitItemId: accountItemIdWithInheritBenefitId(item.debitItemId),
                creditItemId: accountItemIdWithInheritBenefitId(item.creditItemId),
              };
            }),
          },
        ],
        reversal: [
          {
            ...source,
            inheritType: 'none',
            items: items.map((item) => {
              return {
                ...item,
                debitItemId: accountItemIdWithInheritBenefitId(item.debitItemId),
                creditItemId: accountItemIdWithInheritBenefitId(item.creditItemId),
              };
            }),
          },
          {
            ...source,
            inheritType: 'none',
            items: items.map((item) => {
              return ['ItemId', 'Amount', 'SectionId', 'ChangeType'].reduce((x, y) => {
                return { ...x, [`debit${y}`]: item[`credit${y}`], [`credit${y}`]: item[`debit${y}`], };
              }, {});
            }),
          },
        ],
      })[inheritType];
    });
    const createdRefs = [];
    await batch(db, items, (batch, item, i) => {
      const ref = individualAdjustmentJournalsRef.doc();
      createdRefs.push(ref);
      batch.set(ref, { ...item, isInherited: true, period, yearMonth, createdAt: addSeconds(new Date(), i) });
    });
    await createOperationLogs(company, 'individualAdjustmentJournal', 'createIndividualAdjustmentJournal', createdRefs.map(_ => ({ yearMonth, individualAdjustmentJournalId: _.id })));
    toast.success('開始仕訳を引き継ぎました');

    function accountItemIdWithInheritBenefitId (itemId) {
      if(itemId == null) return null;

      const consolidationAccountItem = consolidationAccountItemsById[itemId];
      const category = getCategory(consolidationAccountItem);
      return category?.type === 'bs' ? itemId : consolidationAccountItems.find(_ => _.name.startsWith('繰越利益'))?.id || consolidationAccountItems.find(_ => getCategory(_)?.name === '繰越利益剰余金')?.id;
    }
  };
  const onClickDeleteInheritedJournals = async () => {
    if(!window.confirm('前期末から引き継いだ開始仕訳を削除します。。よろしいですか？')) return;

    await batch(db, individualAdjustmentJournals.filter(_ => _.isInherited), (batch, _) => batch.delete(_.ref));
    toast.success('前期末から引き継いだ開始仕訳を削除しました。');
  };
  const onDrop = async ({ addedIndex, removedIndex }) => {
    const newIds = arrayMoveImmutable(rows, removedIndex, addedIndex).map(_ => _.id);
    await batch(db, newIds, (batch, id, index) => {
      batch.update(company.ref.collection('individualAdjustmentJournals').doc(id), { index });
    });
  };
  const beforeDeleteImport = async (_import) => {
    const data = await getCollectionData(individualAdjustmentJournalsRef.where('importId', '==', _import.id));
    await batch(db, data, (batch, _) => batch.delete(_.ref));
  };
  const deleteImportDisabled = (importId) => {
    return isLockedMonth || (
      company.usesApprovement && individualAdjustmentJournals.filter(_ => _.importId === importId).some(_ => _.approvedAt != null)
    );
  };
  const alerts = computeAlerts(company, individualAdjustmentJournals);
  const onClickAllApprove = async (isNot = false) => {
    const targetIds = isNot ? selectedIds : selectedIds.filter(_ => lastOperatorsById[_]?.id !== user.id);
    const skippedCount = selectedIds.length - targetIds.length;
    if(targetIds.length === 0) {
      toast.error('自己承認禁止により、承認できる仕訳がありません');
      return;
    }
    if(!window.confirm(`${targetIds.length}件を承認${isNot ? '取消' : ''}します。${skippedCount > 0 ? `（自己承認禁止により${skippedCount}件スキップ）` : ''}よろしいですか？`)) return;

    await batch(db, targetIds, (b, _) => b.update(individualAdjustmentJournalsRef.doc(_), {
      approvedAt: isNot ? null : new Date(),
      approvedBy: isNot ? null : pick(user, ['email', 'displayName', 'id']),
    }));
    toast.success(`承認${isNot ? '取消' : ''}しました${skippedCount > 0 ? `（${skippedCount}件スキップ）` : ''}`);
  };
  const onClickBatchDelete = async () => {
    if(!window.confirm(`${selectedIds.length}件を削除します。よろしいですか？`)) return;

    await batch(db, selectedIds, (b, _) => b.delete(company.ref.collection('individualAdjustmentJournals').doc(_)));
    toast.success(`削除しました`);
    setSelectedIds([]);
  };

  return props.translate(
    <div className="company-individual-adjustments">
      <Alerts alerts={alerts} />
      {
        isSelecting ? (
          <div className="mb-1 d-flex justify-content-start align-items-center gap-1">
            <div>{selectedIds.length} 件を選択中</div>
            <ProgressButton color="danger" process={onClickBatchDelete} disabled={isLockedMonth}>
              削除
            </ProgressButton>
            <ModalButton label="他の年月にコピー" Modal={ModelFormModal} modalProps={{ values: { period, yearMonth, }, fields: batchCloneFields({ periodOptions, company, fiscalYearOfPeriod, }), title: '他の年月にコピー', onSubmit: onSubmitCloneToOther, submitLabel: '他の年月にコピー', }} data-operation-type="write" />
            {
              company.usesApprovement && canApprove && (
                <Fragment>
                  <ProgressButton process={onClickAllApprove.bind(null, false)} color="success" disabled={isLockedMonth}>
                    <span className={`fas fa-check mr-1`} />
                    承認
                  </ProgressButton>
                  <ProgressButton process={onClickAllApprove.bind(null, true)} color="warning" disabled={isLockedMonth}>
                    <span className={`fas fa-times mr-1`} />
                    承認取消
                  </ProgressButton>
                </Fragment>
              )
            }
          </div>
        ) : (
          <div className="mb-3 d-flex justify-content-between align-items-end gap-1">
            <div className="d-flex justify-content-start align-items-end gap-1">
              {company.usesApprovement && <QuerySelector simple width={200} paramName="approvements" isMulti options={approvementOptions} label="仕訳承認で絞込み" />}
            </div>
            <div className="d-flex justify-content-end align-items-end gap-1">
              {
                individualAdjustmentJournals.some(_ => _.isInherited) ? (
                  <ProgressButton color="danger" icon={<span className="fas fa-trash" />} process={onClickDeleteInheritedJournals} disabled={isLockedMonth}>
                    前期末から引き継いだ開始仕訳を削除
                  </ProgressButton>
                ) : (
                  <ProgressButton process={onClickInheritFromPrevEnd} disabled={isLockedMonth}>
                    前期末から開始仕訳を引き継ぐ
                  </ProgressButton>
                )
              }
              <ModalButton label={<span><span className="fas fa-list-alt mr-1" />テンプレート</span>} title="修正仕訳テンプレート" content={_ => <IndividualAdjustmentJournalTemplates {...{ user, company, relatedCompany, subsidiaryId, period, yearMonth, isLockedMonth, consolidationAccountItems: mappedConsolidationAccountItems, sections: tipSections, changeTypes, customChangeFields: allCustomChangeFields, ordersByModelType, individualAdjustmentJournalsRef, }} />} modalProps={{ style: { minWidth: 1100 } }} />
              <ModalButton label="他の年月からコピー" Modal={ModelFormModal} modalProps={{ values: { period, yearMonth, }, fields: batchCloneFields({ periodOptions, company, fiscalYearOfPeriod, }), title: '他の年月からコピー', onSubmit: onSubmitBatchClone, submitLabel: 'コピー', }} disabled={isLockedMonth} data-operation-type="write" />
              <ImportButton processValidatedRows={processValidatedRows} processRow={processRow} documentName="individualAdjustmentJournal" disabled={isLockedMonth} validateRow={validateRow} beforeDeleteImport={beforeDeleteImport} importKey={`individualAdjustmentJournals__${relatedCompany.id}__${yearMonth}`} deleteImportConfirmMessage="インポートされた仕訳も削除されます。よろしいですか？" deleteImportDisabled={deleteImportDisabled} onComplete={async _ => { const refs = importedRefsRef.current; importedRefsRef.current = []; await createOperationLogs(company, 'individualAdjustmentJournal', 'createIndividualAdjustmentJournal', refs.map(_ => ({ yearMonth, individualAdjustmentJournalId: _.id }))); }} />
              <ExportButton screenName="修正仕訳" targetCompany={relatedCompany} rows={rowsForExport(relatedCompany, individualAdjustmentJournals, sections, accountItems, consolidationAccountItems, allCustomChangeFields)} />
              <AddButton itemRef={individualAdjustmentJournalsRef.doc()} processValues={_ => ({ ..._, subsidiaryId, period, yearMonth, changeTypes: uniq(_.items.flatMap(_ => [_.debitChangeType, _.creditChangeType]).filter(_ => _)), })} FormModal={IndividualAdjustmentJournalFormModal} initialValues={{ items: [{}], }} formProps={{ consolidationAccountItems: mappedConsolidationAccountItems, sections: tipSections, relatedCompany, customChangeFields: allCustomChangeFields, ordersByModelType, }} disabled={isLockedMonth} onFinish={_ => createOperationLog(company, 'individualAdjustmentJournal', 'createIndividualAdjustmentJournal', { yearMonth, individualAdjustmentJournalId: _.id })} />
            </div>
          </div>
        )
      }
      {filtersContent}
      <div>
        <table className="table sticky-table">
          <thead className="thead-light text-center">
            <tr>
              <th style={{ minWidth: 75, }}>
                {
                  (isAllSelelected => (
                    <Input
                      type="checkbox"
                      className="position-relative m-0"
                      checked={isAllSelelected}
                      onChange={_ => isAllSelelected ? setSelectedIds([]) : setSelectedIds(filteredRows.map(_ => _.id))}
                    />
                  ))(difference(filteredRows.map(_ => _.id), selectedIds).length === 0)
                }
              </th>
              <th style={{ minWidth: 200 }}>借方</th>
              <th style={{ minWidth: 200 }}>貸方</th>
              <th style={{ minWidth: 150, maxWidth: 300, }}>説明</th>
              <th style={{ minWidth: 100 }}></th>
            </tr>
          </thead>
          <Container
            dragHandleSelector={`.drag-handle`}
            onDrop={onDrop}
            onDragStart={_ => toggleDragging(true)}
            onDragEnd={_ => toggleDragging(false)}
            dropPlaceholder={{ style: { background: 'eee', } }}
            render={(ref) => (
              <tbody ref={ref}>
                {
                  filteredRows.map((row) => {
                    const { id, ref, inheritType = 'none', mappedItems, description, copiedAt, isUpdatedAfterCopy, approvedAt, approvedBy, } = row;
                    const onClickCopyUrl = () => {
                      const { location } = window;
                      const url = `${location.origin}/companies/${company.id}/relatedCompanies/${relatedCompany.id}/individualAdjustments?${qs.stringify({ period, yearMonth, individualAdjustmentJournalId: id })}`;
                      copy(url);
                      toast.success('URLをクリップボードにコピーしました');
                    };
                    const onClickCopy = async () => {
                      if(!window.confirm('本当にコピーしますか？')) return;

                      const newRef = individualAdjustmentJournalsRef.doc();
                      await newRef.set({
                        ...pick(row, ['inheritType', 'description', 'items', 'changeTypes']),
                        index: row.index + 0.01,
                        subsidiaryId,
                        period,
                        yearMonth,
                        copiedAt: new Date(),
                        createdAt: new Date(),
                        description: (row.description || '') + 'のコピー',
                      });
                      await createOperationLog(company, 'individualAdjustmentJournal', 'createIndividualAdjustmentJournal', { yearMonth, individualAdjustmentJournalId: newRef.id, sourceIndividualAdjustmentJournalId: id });
                      toast.success('コピーしました');
                    };
                    const onClickApprove = async () => {
                      if(!window.confirm(`本当に承認${approvedAt != null ? '取消' : ''}しますか？`)) return;

                      await ref.update({
                        approvedAt: approvedAt != null ? null : new Date(),
                        approvedBy: approvedAt != null ? null : pick(user, ['email', 'displayName', 'id']),
                      });
                      toast.success(`承認${approvedAt != null ? '取消' : ''}しました`);
                    };
                    const processValues = (values) => {
                      const _values = { ...values, changeTypes: uniq(values.items.flatMap(_ => [_.debitChangeType, _.creditChangeType]).filter(_ => _)), };
                      return copiedAt == null ? (
                        _values
                      ) : isEqual(row.items?.map(_ => pickBy(_, _ => _)), values.items?.map(_ => pickBy(_, _ => _))) ? (
                        _values
                      ) : { ..._values, isUpdatedAfterCopy: true, };
                    };

                    return (
                      <Draggable
                        key={id}
                        render={() => {
                          return (
                            <tr key={id} ref={queryParams.individualAdjustmentJournalId === id ? elm : undefined} style={{ backgroundColor: queryParams.individualAdjustmentJournalId === id ? focusColor : undefined, display: !isDragging && 'table-row', }}>
                              <td style={{ width: 75 }}>
                                <div className="d-flex align-items-start gap-2">
                                  <div>
                                    <div className={`drag-handle text-muted cursor-pointer`}>
                                      <span className="fas fa-grip-vertical" />
                                    </div>
                                    <Input
                                      type="checkbox"
                                      className="position-relative m-0"
                                      checked={selectedIds.includes(id)}
                                      onChange={_ => (selectedIds.includes(id) ? unselectId(id) : selectId(id))}
                                    />
                                  </div>
                                  <div className="d-flex flex-column gap-1">
                                    {copiedAt != null && !isUpdatedAfterCopy && <span data-tooltip-id={`tooltip-${id}`} data-tooltip-content="コピーされてから変更がありません" className="fas fa-exclamation-triangle text-warning large" />}
                                    {inheritType !== 'none' && <div className="badge badge-info">{inheritTypes[inheritType].label}</div>}
                                    <Tooltip id={`tooltip-${id}`} />
                                  </div>
                                </div>
                                {
                                  user.dev && (
                                    <a href={`https://console.firebase.google.com/u/0/project/freee-consolidation-staging/firestore/data/~2Fcompanies~2F${company.id}~2FindividualAdjustmentJournals~2F${id}`} target="_blank">F</a>
                                  )
                                }
                              </td>
                              <td style={{ minWidth: 200, }}>
                                {
                                  mappedItems.map((item, i) => {
                                    const { debitAccountItem, debitSection, debitAmount, debitChangeType, } = item;
                                    return (
                                      <div key={i} className="d-flex justify-content-between">
                                        <div>{debitAccountItem?.name}</div>
                                        <div className="text-muted">{debitSection?.name}</div>
                                        <div className="text-muted">{changeTypes[debitChangeType]?.label}</div>
                                        <div>{debitAmount != null && numberFormat(relatedCompany.currency, debitAmount)}</div>
                                      </div>
                                    );
                                  })
                                }
                              </td>
                              <td style={{ minWidth: 200, }}>
                                {
                                  mappedItems.map((item, i) => {
                                    const { creditAccountItem, creditSection, creditAmount, creditChangeType, } = item;
                                    return (
                                      <div key={i} className="d-flex justify-content-between">
                                        <div>{creditAccountItem?.name}</div>
                                        <div className="text-muted">{creditSection?.name}</div>
                                        <div className="text-muted">{changeTypes[creditChangeType]?.label}</div>
                                        <div>{creditAmount != null && numberFormat(relatedCompany.currency, creditAmount)}</div>
                                      </div>
                                    );
                                  })
                                }
                              </td>
                              <td style={{ minWidth: 150, maxWidth: 300, }}>
                                <AutoLinkText>
                                  {description}
                                </AutoLinkText>
                              </td>
                              <td style={{ minWidth: 100 }} className="text-nowrap text-right">
                                {
                                  user.admin && (
                                    <ProgressButton color="link" size="sm" process={_ => row.ref.update({ isInherited: !row.isInherited })}>
                                      (inherited{row.isInherited ? 'を解除' : 'にする'})
                                    </ProgressButton>
                                  )
                                }
                                <Button outline onClick={onClickCopyUrl}>
                                  <span className="fas fa-link" />
                                </Button>
                                <ProgressButton className="ml-1" process={onClickCopy} disabled={isLockedMonth}>
                                  <span className="fas fa-copy mr-1" />
                                  コピー
                                </ProgressButton>
                                <EditButton className="ml-1" itemRef={ref} FormModal={IndividualAdjustmentJournalFormModal} formProps={{ consolidationAccountItems: mappedConsolidationAccountItems, sections: tipSections, values: row, relatedCompany, customChangeFields: allCustomChangeFields, ordersByModelType, }} disabled={approvedAt != null || isLockedMonth} processValues={processValues} onFinish={_ => createOperationLog(company, 'individualAdjustmentJournal', 'updateIndividualAdjustmentJournal', { yearMonth, individualAdjustmentJournalId: id })} />
                                <DeleteButton item={row} itemRef={ref} className="ml-1" disabled={approvedAt != null || isLockedMonth} />
                                <OperationHistoryButton types={['createIndividualAdjustmentJournal', 'updateIndividualAdjustmentJournal']} payloadFilters={{ individualAdjustmentJournalId: id }} color="secondary" className="ml-1" />
                                {
                                  company.usesApprovement && (
                                    <div className="d-flex flex-column align-items-end gap-1 mt-1">
                                      {
                                        canApprove && (
                                          <ProgressButton size="sm" process={onClickApprove} color={approvedAt != null ? 'warning' : 'success'} disabled={isLockedMonth || (approvedAt == null && lastOperatorsById[id]?.id === user.id)} title={approvedAt == null && lastOperatorsById[id]?.id === user.id ? '自己承認は禁止されています' : undefined}>
                                            <span className={`fas fa-${approvedAt != null ? 'times' : 'check'} mr-1`} />
                                            承認{approvedAt != null && '取消'}
                                          </ProgressButton>
                                        )
                                      }
                                      {approvedAt && <div className="text-muted small d-flex flex-column"><span>{formatDate(approvedAt.toDate(), 'yyyy/MM/dd HH:mm:ss')}</span><span>by {approvedBy.displayName}</span></div>}
                                    </div>
                                  )
                                }
                              </td>
                            </tr>
                          );
                        }}
                      />
                    );
                  })
                }
              </tbody>
            )}
          />
        </table>
      </div>
    </div>
  );
});
